import React, { useEffect, useMemo, useRef, useState } from "react";
import { ROUTES } from "../../../constants/routes-path";
import { PROCESS_TYPES, processesService } from "../../../services/processesService";
import {
    Button,
    Flex,
    FormControl,
    FormErrorMessage,
    FormLabel,
    IconButton,
    Input,
    LightMode,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    Select,
    Stack,
    Text,
    Textarea,
    useColorModeValue
} from "@chakra-ui/react";
import CardHeader from "../../../components/Card/CardHeader";
import Loading from "../../../components/Loading/Loading";
import { AddIcon, DeleteIcon, SearchIcon, DownloadIcon } from "@chakra-ui/icons";
import { trafficViolationService } from "../../../services/trafficViolationService";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useCustomToast } from "../../../components/CustomToast/CustomToast";
import {
    aitMask,
    checkErrors,
    hasError,
    isValidAitNumber,
    isValidCEP,
    maskCPF,
    parseApiDateTime
} from "../../../utils/forms-utils";
import { fetchAddressByCEP, formatCEP, formatPhoneNumber, ufs, validateCPF, validateEmail } from "../../../utils";
import Card from "../../../components/Card/Card";
import CardBody from "../../../components/Card/CardBody";
import { IoIosArrowDown } from "react-icons/io";
import ConfirmDialog from "../../../components/Dialog/ConfirmDialog";
import { toServerDate } from "../../../utils/traffic-violation-utils";
import { FileAttachmentMultipleInput } from "../../../components/FileAttachmentInput/FileAttachmentMultipleInput";
import { configService } from "../../../services/configService";
import { vehicleService } from "../../../services/vehicleService";
import moment from "moment";
import "moment/locale/pt-br";
import "react-datetime/css/react-datetime.css";
import DateTime from "react-datetime";
import { DATE_FORMAT, DATE_FORMAT_ISO, LOCALE_PT_BR, TIME_FORMAT } from "constants/date-time";

const ProcessNew = ({ initialData = null, onSubmit = null, isEdit = false, loading = false, onAttachmentRemove = null }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const showToast = useCustomToast();
    const textColor = useColorModeValue("gray.700", "white");
    const cardSpacing = "20px";
    const formSpacing = "30px";
    const translationKey = "process";
    const formTranslationKey = `${translationKey}.form`;
    const formDirection = { sm: "column", md: "row" };
    const [attachments, setAttachments] = useState(
        Array.from({ length: 3 }, () => ({ tipo: "", observacoes: "" }))
    );
    const [attachmentFileName, setAttachmentFileName] = useState(null);
    const [originalFileName, setOriginalFileName] = useState(null);
    const attachmentRef = useRef(undefined);
    const [formData, setFormData] = useState(undefined);
    const [documentTypes, setDocumentTypes] = useState([]);
    const [inputError, setInputError] = useState({});
    const shouldSearchAitInitialRef = useRef(false);
    const dialogRef = useRef(undefined);
    const [selectedAttachment, setSelectedAttachment] = useState(undefined);
    const [aitInfoCheck, setAitInfoCheck] = useState(undefined);
    const [denyNaOnNp, setDenyNaOnNp] = useState(false);
    const [types, setTypes] = useState(undefined);
    const [duplicateSameTypeProcess, setDuplicateSameTypeProcess] = useState(null);
    const ids = useMemo(
        () => ({
            aitNumber: "ait-number",
            infracaoNomeProprietario: "infracao-nome-proprietario",
            infracaoPlacaVeiculo: "infracao-placa-veiculo",
            infracaoUfPlacaVeiculo: "infracao-uf-placa-veiculo",
            infracaoDataInfracao: "infracao-data-infracao",
            idInfracao: "id-infracao",
            infracao: "infracao",
            processTipo: "tipo",
            nomeCondutor: "nome-condutor",
            cpfCnpjCondutor: "cpf-cnpj-condutor",
            telefoneContato: "telefone-contato",
            emailContato: "email-contato",
            cepRequerente: "cep-requerente",
            observacoes: "observacoes",
            dataLimiteJulgamento: "data-limite-julgamento",
            infracaoLogradouro: "logradouro_local_infracao",
            infracaoMarcaModelo: "descricao_marca_modelo_veiculo",
            aitLogradouroRequerente: "logradouro_proprietario_veiculo_ait",
            aitNumeroRequerente: "numero_imovel_proprietario_veiculo_ait",
            aitComplementoRequerente: "complemento_imovel_proprietario_veiculo_ait",
            aitBairroRequerente: "bairro_imovel_proprietario_veiculo_ait",
            aitMunicipioRequerente: "nome_municipio_imovel_proprietario_veiculo_ait",
            aitEstadoRequerente: "uf_imovel_proprietario_veiculo_ait",
            registroCnhCondutor: "registro_cnh_condutor",
        }),
        []
    );

    const initFormData = () => {
        return {
            [ids.aitNumber]: {
                value: initialData?.infracao?.numero_ait || "",
                validations: [],
            },
            [ids.infracaoPlacaVeiculo]: {
                value: initialData?.infracao?.placa_veiculo || "",
                validations: [],
            },
            [ids.infracaoUfPlacaVeiculo]: {
                value: initialData?.infracao?.uf_placa_veiculo || "",
                validations: [],
            },
            [ids.infracaoNomeProprietario]: {
                value: initialData?.infracao?.nome_proprietario_veiculo_ait || "",
                validations: [],
            },
            [ids.infracaoMarcaModelo]: {
                value: initialData?.infracao?.descricao_marca_modelo_veiculo || "",
                validations: [],
            },
            [ids.infracaoLogradouro]: {
                value: initialData?.infracao?.logradouro_local_infracao || "",
                validations: [],
            },
            [ids.infracao]: {
                value: initialData ? formatarInfracao(initialData?.infracao) : "",
                validations: [],
            },
            [ids.infracaoDataInfracao]: {
                value: initialData ? formattedDate(initialData?.infracao?.data_infracao) : "",
                validations: [],
            },
            [ids.idInfracao]: {
                value: initialData?.infracao?.id || null,
                validations: [],
            },
            [ids.processTipo]: {
                value: initialData?.tipo || "",
                validations: [{ required: true }],
            },
            [ids.nomeCondutor]: {
                value: initialData?.nome_condutor || "",
                validations: [{ required: true }],
            },
            [ids.cpfCnpjCondutor]: {
                value: initialData?.cpf_cnpj_condutor || "",
                validations: [{ required: true }],
            },
            [ids.telefoneContato]: {
                value: initialData ? formatPhoneNumber(initialData?.telefone_contato) : "",
                validations: [],
            },
            [ids.emailContato]: {
                value: initialData?.email_contato || "",
                validations: [],
            },
            [ids.cepRequerente]: {
                value: initialData?.cep_requerente ? formatCEP(initialData?.cep_requerente) : "",
                validations: [{ required: true }],
            },
            [ids.observacoes]: {
                value: initialData?.observacoes || "",
                validations: [],
            },
            [ids.aitLogradouroRequerente]: {
                value: initialData?.logradouro_requerente || "",
                validations: [{ required: true }],
            },
            [ids.aitNumeroRequerente]: {
                value: initialData?.numero_requerente || "",
                validations: [{ required: true }],
            },
            [ids.aitComplementoRequerente]: {
                value: initialData?.complemento_requerente || "",
                validations: [],
            },
            [ids.aitBairroRequerente]: {
                value: initialData?.bairro_requerente || "",
                validations: [{ required: true }],
            },
            [ids.aitMunicipioRequerente]: {
                value: initialData?.municipio_requerente || "",
                validations: [{ required: true }],
            },
            [ids.aitEstadoRequerente]: {
                value: initialData?.uf_requerente || "",
                validations: [{ required: true }],
            },
            [ids.dataLimiteJulgamento]: {
                value: initialData?.data_limite_julgamento
                    ? moment(initialData.data_limite_julgamento, DATE_FORMAT).format(DATE_FORMAT_ISO)
                    : getDataLimiteJulgamentoPadrao(),
                validations: [{ required: true }],
            },
            [ids.registroCnhCondutor]: {
                value: initialData?.registro_cnh_condutor || "",
                validations: []
            }
        };
    };

    const maskMap = {
        [ids.aitNumber]: aitMask,
        [ids.cpfCnpjCondutor]: maskCPF,
        [ids.telefoneContato]: formatPhoneNumber,
        [ids.cepRequerente]: formatCEP,
    };

    const validatorMap = {
        [ids.aitNumber]: {
            validate: (v) => !v || isValidAitNumber(v),
            errorKey: 'ait-format',
        },
        [ids.cpfCnpjCondutor]: {
            validate: validateCPF,
            errorKey: 'cpf-format',
        },
        [ids.telefoneContato]: {
            validate: (v) => v.length >= 15,
            errorKey: 'phone-number-invalid',
        },
        [ids.cepRequerente]: {
            validate: isValidCEP,
            errorKey: 'cep-format',
        },
        [ids.emailContato]: {
            validate: validateEmail,
            errorKey: 'email-invalid',
        }
    };

    const isFormInvalid = () => {
        const attachmentsAreValid = attachments.every(value => value.tipo !== '')
        const formInInvalid = formData && inputError ? hasError({ formData, inputError }) : true;
        return formInInvalid || !attachmentsAreValid;
    }

    const redirectToList = () => {
        navigate(ROUTES.transportManagementPath + ROUTES.processes);
    };

    const handleInputChange = async (e) => {
        const { id, value } = e.target;

        const maskedValue = maskMap[id] ? maskMap[id](value) : value;

        const newFormData = {
            ...formData,
            [id]: { ...formData[id], value: maskedValue },
        };

        const idsToCheck = [id];

        let newInputError = { ...inputError };
        if (id === ids.processTipo) {
            [ids.nomeCondutor, ids.cpfCnpjCondutor].forEach(key => {
                if (newInputError[key]) delete newInputError[key];
            });
        }

        if (id === ids.processTipo && newFormData[ids.processTipo].value?.toLowerCase() === PROCESS_TYPES.NA) {
            const now = new Date();
            if (denyNaOnNp && aitInfoCheck?.dataEmissaoNp && aitInfoCheck.dataEmissaoNp.getTime() <= now.getTime()) {
                newFormData[ids.processTipo].value = PROCESS_TYPES.NP.toLowerCase();
                showToast({ title: t("process.messages.errors.invalid-type"), status: "error" });
            } else if (aitInfoCheck?.dataVencimentoDesconto) {
                newFormData[ids.processTipo].value = "";
                showToast({ title: t("process.messages.errors.invalid-type-na"), status: "error" });
            } else if (!aitInfoCheck?.dataEmissaoNa) {
                newFormData[ids.processTipo].value = "";
                showToast({ title: t("process.messages.errors.invalid-ait"), status: "error" });
            } else if (aitInfoCheck && aitInfoCheck.status?.toUpperCase() !== "VALIDADA") {
                newFormData[ids.processTipo].value = "";
                showToast({ title: t("process.messages.errors.invalid-ait"), status: "error" });
            }
        } else if (id === ids.processTipo && newFormData[ids.processTipo].value?.toLowerCase() === PROCESS_TYPES.NP) {
            if (aitInfoCheck && (!aitInfoCheck.dataEmissaoNp || !aitInfoCheck.dataEnvioNp || aitInfoCheck.status?.toUpperCase() !== "VALIDADA")) {
                newFormData[ids.processTipo].value = "";
                showToast({ title: t("process.messages.errors.invalid-np"), status: "error" });
            }
        }

        if (id === ids.processTipo) {
            if (value.toLowerCase() === PROCESS_TYPES.IC || newFormData[ids.infracaoPlacaVeiculo].value === '') {
                newFormData[ids.nomeCondutor].value = '';
                newFormData[ids.cpfCnpjCondutor].value = '';
            } else {
                const vehicle = await vehicleService.findByPlate(newFormData[ids.infracaoPlacaVeiculo].value);
                newFormData[ids.nomeCondutor].value = vehicle.nomeProprietario?.trim();
                if (newFormData[ids.infracaoNomeProprietario].value?.trim().length > 0 &&
                    newFormData[ids.infracaoNomeProprietario].value.trim() === vehicle?.nomeProprietario?.trim()) {
                    newFormData[ids.cpfCnpjCondutor].value = maskCPF(vehicle?.cpfCnpjProprietario?.trim() ?? "");
                }
            }
        }

        if (id === ids.cepRequerente && maskedValue.length === 9) {
            await fetchAddressByCEP(maskedValue.replace("-", "")).then(addr => {
                newFormData[ids.aitLogradouroRequerente].value = addr.logradouro;
                newFormData[ids.aitComplementoRequerente].value = addr.complemento;
                newFormData[ids.aitBairroRequerente].value = addr.bairro;
                newFormData[ids.aitMunicipioRequerente].value = addr.localidade;
                newFormData[ids.aitEstadoRequerente].value = addr.uf;
            });
            idsToCheck.push(ids.aitLogradouroRequerente,
                ids.aitBairroRequerente,
                ids.aitMunicipioRequerente,
                ids.aitEstadoRequerente);
        }
        setFormData(newFormData);

        idsToCheck.forEach(itemId => {
            newInputError = validateErrors(newFormData, itemId, newFormData[itemId].value, newInputError);
        });

        setInputError(newInputError);
    };

    const handleJudgementLimitDateChange = (date) => {
        const m = moment(date);
        const iso = m.isValid() ? m.format(DATE_FORMAT_ISO) : "";

        const updated = {
            ...formData,
            [ids.dataLimiteJulgamento]: {
                ...formData[ids.dataLimiteJulgamento],
                value: iso,
            },
        };

        setFormData(updated);

        setInputError((prev) =>
            checkErrors({ formData: updated, inputError: prev, name: ids.dataLimiteJulgamento })
        );
    };

    const validateErrors = (newFormData, id = null, maskedValue = null, prevErrors = {}) => {
        const validator = validatorMap[id];
        let formValidationErrors = checkErrors({ formData: newFormData, inputError: { ...prevErrors }, name: id });

        if (maskedValue !== null && validator) {
            if (maskedValue.length > 0 && !validator.validate(maskedValue)) {
                formValidationErrors[id] = t(`${translationKey}.messages.errors.${validator.errorKey}`);
            } else if (formValidationErrors[id] === t(`${translationKey}.messages.errors.${validator.errorKey}`)) {
                delete formValidationErrors[id];
            }
        }

        return { ...formValidationErrors };
    };

    const formattedDate = (date) => date?.slice(0, 10);

    const getDataLimiteJulgamentoPadrao = () => {
        const date = moment().add(30, 'days');
        const isoWeekday = date.isoWeekday();
        if (isoWeekday === 7) {
            date.add(1, 'days');
        } else if (isoWeekday === 6) {
            date.add(2, 'days');
        }
        return date.format(DATE_FORMAT_ISO);
    };

    function formatarInfracao(infracao: any): string {
        const { codigo_tipo_infracao, desdobramento_tipo_infracao, descricao_tipo_infracao } = infracao;
        return `${codigo_tipo_infracao}-${desdobramento_tipo_infracao} ${descricao_tipo_infracao}`;
    }

    const handleAitSearch = async () => {
        try {
            const result = await trafficViolationService.findByAITNumber(
                formData[ids.aitNumber].value
            );
            processesService.listProcessTypes({ "ait": formData[ids.aitNumber].value, "isEdit": isEdit })
                .then(setTypes);

            const cepReq = initialData?.cep_requerente ?? result.cep_imovel_proprietario_veiculo_penalidade ?? result.cep_imovel_proprietario_veiculo_ait;
            const infracaoData = {
                [ids.infracaoNomeProprietario]: {
                    ...formData[ids.infracaoNomeProprietario],
                    value: result.nome_proprietario_veiculo_ait?.trim(),
                },
                [ids.infracaoPlacaVeiculo]: {
                    ...formData[ids.infracaoPlacaVeiculo],
                    value: result.placa_veiculo?.trim(),
                },
                [ids.infracaoUfPlacaVeiculo]: {
                    ...formData[ids.infracaoUfPlacaVeiculo],
                    value: result.uf_placa_veiculo?.trim(),
                },
                [ids.infracaoDataInfracao]: {
                    ...formData[ids.infracaoDataInfracao],
                    value: formattedDate(result.data_infracao),
                },
                [ids.infracaoLogradouro]: {
                    ...formData[ids.infracaoLogradouro],
                    value: result.logradouro_local_infracao?.trim(),
                },
                [ids.idInfracao]: {
                    ...formData[ids.idInfracao],
                    value: result.id || 0,
                },
                [ids.infracao]: {
                    ...formData[ids.infracao],
                    value: formatarInfracao(result),
                },
                [ids.infracaoMarcaModelo]: {
                    ...formData[ids.infracaoMarcaModelo],
                    value: result.descricao_marca_modelo_veiculo,
                },
                [ids.cepRequerente]: {
                    ...formData[ids.cepRequerente],
                    value: cepReq ? formatCEP(cepReq) : formData[ids.cepRequerente].value ?? null,
                },
                [ids.aitLogradouroRequerente]: {
                    ...formData[ids.aitLogradouroRequerente],
                    value: initialData?.logradouro_requerente ?? result.logradouro_proprietario_veiculo_penalidade ?? result.logradouro_proprietario_veiculo_ait ?? formData[ids.aitLogradouroRequerente].value,
                },
                [ids.aitNumeroRequerente]: {
                    ...formData[ids.aitNumeroRequerente],
                    value: initialData?.numero_requerente ?? result.numero_imovel_proprietario_veiculo_penalidade ?? result.numero_imovel_proprietario_veiculo_ait ?? formData[ids.aitNumeroRequerente].value,
                },
                [ids.aitComplementoRequerente]: {
                    ...formData[ids.aitComplementoRequerente],
                    value: initialData?.complemento_requerente ?? result.complemento_imovel_proprietario_veiculo_penalidade ?? result.complemento_imovel_proprietario_veiculo_ait ?? formData[ids.aitComplementoRequerente].value,
                },
                [ids.aitBairroRequerente]: {
                    ...formData[ids.aitBairroRequerente],
                    value: initialData?.bairro_requerente ?? result.bairro_imovel_proprietario_veiculo_penalidade ?? result.bairro_imovel_proprietario_veiculo_ait ?? formData[ids.aitBairroRequerente].value,
                },
                [ids.aitMunicipioRequerente]: {
                    ...formData[ids.aitMunicipioRequerente],
                    value: initialData?.cidade_requerente ?? result.nome_municipio_imovel_proprietario_veiculo_penalidade ?? result.nome_municipio_imovel_proprietario_veiculo_ait ?? formData[ids.aitMunicipioRequerente].value,
                },
                [ids.aitEstadoRequerente]: {
                    ...formData[ids.aitEstadoRequerente],
                    value: initialData?.uf_requerente ?? result.uf_imovel_proprietario_veiculo_penalidade ?? result.uf_imovel_proprietario_veiculo_ait ?? formData[ids.aitEstadoRequerente].value,
                },
            };

            if (!initialData) {
                infracaoData[ids.dataLimiteJulgamento] = {
                    ...formData[ids.dataLimiteJulgamento],
                    value: getDataLimiteJulgamentoPadrao(),
                };
            }

            const toAitInfoCheck = (result: any) => ({
                dataEmissaoNa: parseApiDateTime(result?.data_emissao_notificacao_autuacao),
                dataEmissaoNp: parseApiDateTime(result?.data_emissao_notificacao_penalidade),
                dataEnvioNp: parseApiDateTime(result?.data_envio_notificacao_penalidade),
                dataVencimentoDesconto: parseApiDateTime(result?.data_vencimento_desconto),
                status: result?.status ?? null,
            });

            const selectedType = formData[ids.processTipo].value;

            // todo: verificar se é necessário buscar o veículo pela placa - inicio - jota.freitas
            if (selectedType !== PROCESS_TYPES.IC && result) {
                infracaoData[ids.nomeCondutor] = { ...formData[ids.nomeCondutor], value: result.nome_proprietario_veiculo_ait?.trim() };
                infracaoData[ids.cpfCnpjCondutor] = { ...formData[ids.cpfCnpjCondutor], value: maskCPF(result.cpf_cnpj_proprietario_veiculo_ait?.trim()) };
                infracaoData[ids.cpfCnpjCondutor] = { ...formData[ids.cpfCnpjCondutor], value: maskCPF(result.cpf_cnpj_proprietario_veiculo_ait?.trim()) };
            }

            /* if (!initialData) {
                if (selectedType !== PROCESS_TYPES.IC && result) {
                    const vehicle = await vehicleService.findByPlate(infracaoData['infracao-placa-veiculo'].value);
                    infracaoData[ids.nomeCondutor] = { ...formData[ids.nomeCondutor], value: vehicle?.nomeProprietario?.trim() };

                    if (result.nome_proprietario_veiculo_ait?.trim().length > 0 && vehicle?.cpfCnpjProprietario && result.nome_proprietario_veiculo_ait.trim() === vehicle?.nomeProprietario?.trim()) {
                        infracaoData[ids.cpfCnpjCondutor] = { ...formData[ids.cpfCnpjCondutor], value: maskCPF(vehicle?.cpfCnpjProprietario?.trim()) };
                        validateErrors(
                            { ...formData, [ids.cpfCnpjCondutor]: infracaoData[ids.cpfCnpjCondutor] },
                            ids.cpfCnpjCondutor,
                            vehicle?.cpfCnpjProprietario?.trim() ?? ""
                        );
                    }
                } else {
                    infracaoData[ids.nomeCondutor] = { ...formData[ids.nomeCondutor], value: '' };
                    infracaoData[ids.cpfCnpjCondutor] = { ...formData[ids.cpfCnpjCondutor], value: '' };
                }
            } */
            // todo: verificar se é necessário buscar o veículo pela placa - fim - jota.freitas

            if (selectedType === PROCESS_TYPES.NA) {
                const now = new Date();
                if (denyNaOnNp && result.data_emissao_notificacao_penalidade && toAitInfoCheck.dataEmissaoNp.getTime() <= now.getTime()) {
                    infracaoData[ids.processTipo] = { ...formData[ids.processTipo], value: PROCESS_TYPES.NP.toUpperCase() };
                    showToast({ title: t("process.messages.errors.invalid-type"), status: "error" });
                } else if (result.data_vencimento_desconto) {
                    infracaoData[ids.processTipo] = { ...formData[ids.processTipo], value: "" };
                    showToast({ title: t("process.messages.errors.invalid-type-na"), status: "error" });
                } else if (!result.data_emissao_notificacao_autuacao) {
                    infracaoData[ids.processTipo] = { ...formData[ids.processTipo], value: "" };
                    showToast({ title: t("process.messages.errors.invalid-ait"), status: "error" });
                } else if (result.status?.toUpperCase() !== "VALIDADA") {
                    infracaoData[ids.processTipo] = { ...formData[ids.processTipo], value: "" };
                    showToast({ title: t("process.messages.errors.invalid-ait"), status: "error" });
                }
            } else if (selectedType === PROCESS_TYPES.NP) {
                if (!result.data_emissao_notificacao_penalidade || !result.data_envio_notificacao_penalidade || result.status?.toUpperCase() !== "VALIDADA") {
                    infracaoData[ids.processTipo] = { ...formData[ids.processTipo], value: "" };
                    showToast({ title: t("process.messages.errors.invalid-np"), status: "error" });
                }
            }

            setAitInfoCheck({
                dataEmissaoNa: toAitInfoCheck(result).dataEmissaoNa,
                dataEmissaoNp: toAitInfoCheck(result).dataEmissaoNp,
                dataEnvioNp: toAitInfoCheck(result).dataEnvioNp,
                dataVencimentoDesconto: toAitInfoCheck(result).dataVencimentoDesconto,
                status: toAitInfoCheck(result).status
            });
            setFormData((prev) => ({ ...prev, ...infracaoData }));
        } catch (err) {
            showToast({ title: t(err), status: "error" });
        }
    };

    const fetchData = async () => {
        try {
            processesService.listDocumentTypes()
                .then(setDocumentTypes);
            const config = await configService.get();
            setDenyNaOnNp(config.processes_deny_na_on_np);
            if (initialData) {
                shouldSearchAitInitialRef.current = true;
                setFormData(initFormData(initialData));
                if (initialData?.anexos_processos?.length) {
                    setAttachments(
                        initialData.anexos_processos?.map(attachment => {
                            return { tipo: attachment.id_tipo_documento, observacoes: attachment.observacoes, id: attachment.id }
                        })
                    )
                }
                if (initialData?.anexo_arquivo) {
                    const fileName = initialData?.anexo_arquivo?.split("\\").pop()?.split("/").pop();
                    setAttachmentFileName([fileName]);
                    setOriginalFileName(fileName);
                }
            } else {
                setFormData(initFormData());
            }
        } catch (err) {
            showToast({ title: t(err), status: "error" });
            redirectToList();
        }
    };

    useEffect(() => {
        if (shouldSearchAitInitialRef.current) {
            handleAitSearch();
            shouldSearchAitInitialRef.current = false;
        }
    }, [formData]);

    useEffect(() => {
        if (isEdit && !loading && initialData) {
            fetchData();
            return;
        }
        if (!isEdit) {
            fetchData();
        }

    }, [loading, initialData]);

    useEffect(() => {
        let cancelled = false;
        const checkDuplicateProcessType = async () => {
            if (isEdit || !formData) {
                setDuplicateSameTypeProcess(null);
                return;
            }
            const idInfracao = formData[ids.idInfracao]?.value;
            const tipo = formData[ids.processTipo]?.value;
            if (!idInfracao || !tipo) {
                setDuplicateSameTypeProcess(null);
                return;
            }
            try {
                const list = await processesService.listByInfractionId(idInfracao);
                if (cancelled) return;
                const normalized = String(tipo).trim().toLowerCase();
                const match = list.find(
                    (p) => p?.tipo != null && String(p.tipo).trim().toLowerCase() === normalized
                );
                setDuplicateSameTypeProcess(match ?? null);
            } catch {
                if (!cancelled) setDuplicateSameTypeProcess(null);
            }
        };
        checkDuplicateProcessType();
        return () => {
            cancelled = true;
        };
    }, [isEdit, formData?.[ids.idInfracao]?.value, formData?.[ids.processTipo]?.value]);

    const duplicateWarningTypeLabel = useMemo(() => {
        if (!duplicateSameTypeProcess || !formData) return "";
        const selectedKey = formData[ids.processTipo]?.value;
        const meta = types?.find(
            (ty) => String(ty.key).toLowerCase() === String(selectedKey).toLowerCase()
        );
        return meta
            ? t(`${formTranslationKey}.process-types.${meta.label}`)
            : String(selectedKey || duplicateSameTypeProcess.tipo || "").toUpperCase();
    }, [duplicateSameTypeProcess, formData, types, t, formTranslationKey, ids.processTipo]);

    const toFormData = () => {
        const formValues = {
            numero_ait: formData[ids.aitNumber].value.trim(),
            placa_veiculo: formData[ids.infracaoPlacaVeiculo].value.trim(),
            tipo: formData[ids.processTipo].value.trim(),
            data_limite_julgamento: toServerDate(formData[ids.dataLimiteJulgamento].value),
            observacoes: formData[ids.observacoes].value?.trim() || "",
            cep_requerente: formData[ids.cepRequerente].value.trim(),
            logradouro_requerente: formData[ids.aitLogradouroRequerente].value.trim(),
            numero_requerente: formData[ids.aitNumeroRequerente].value.trim(),
            bairro_requerente: formData[ids.aitBairroRequerente].value.trim(),
            complemento_requerente: formData[ids.aitComplementoRequerente].value?.trim(),
            cidade_requerente: formData[ids.aitMunicipioRequerente].value.trim(),
            uf_requerente: formData[ids.aitEstadoRequerente].value.trim(),
            telefone_contato: formData[ids.telefoneContato].value.trim(),
            email_contato: formData[ids.emailContato].value.trim(),
            cpf_cnpj_condutor: formData[ids.cpfCnpjCondutor].value.trim(),
            nome_condutor: formData[ids.nomeCondutor].value.trim(),
            id_tipo_documento: attachments.map(item => item.tipo || ""),
            id_anexo_processo: attachments.filter(item => item.id).map(item => item.id),
            observacoes_anexo_processo: attachments.map(item => item.observacoes || ""),
            registro_cnh_condutor: formData[ids.registroCnhCondutor].value.trim()
        };

        const data = new FormData();

        data.append("requestData", new Blob([JSON.stringify(formValues)], { type: "application/json" }));

        const files = attachmentRef?.current?.getFiles?.();
        if (files && Array.isArray(files)) {
            files.forEach((file) => {
                data.append("anexos", file);
            });
        }

        return data;
    };

    const handleSave = async () => {
        try {
            const data = toFormData();
            if (isEdit && onSubmit) {
                await onSubmit(data);
            } else {
                await processesService.create(data);
                showToast({
                    title: t("process.messages.success.create"),
                    status: "success",
                });
                redirectToList();
            }
        } catch (err) {
            showToast({ title: t(err), status: "error" });
        }
    };

    function handleAddAttachment() {
        setAttachments((prev) => [
            ...prev,
            { tipo: "", observacoes: "" },
        ]);
    }

    function handleRemoveAttachment(index: number) {
        setSelectedAttachment(index)
        dialogRef.current?.displayDialog({
            title: t("process.list.actions.remove-process-attachment"),
            message: t("system.actions.remove-confirm"),
        })
    }

    const handleConfirmRemoveAttachment = () => {
        setAttachments((prev) => prev.filter((_, i) => i !== selectedAttachment));
    }

    function handleAttachmentChange(index: number, field: "tipo" | "observacoes", value: string) {
        setAttachments((prev) =>
            prev.map((item, i) => {
                if (i === index) {
                    return { ...item, [field]: value };
                }
                return item;
            })
        );
    }

    const aitDetail = () => {
        navigate(ROUTES.trafficViolationsDetailsPath.replace(":id", initialData?.id_infracao));
    }

    const removeProcessAttachment = async () => {
        if (onAttachmentRemove && attachmentFileName) {
            onAttachmentRemove().then(() => {
                setAttachmentFileName(null);
                setOriginalFileName(null);
            }).catch(() => { });
        }
    }

    const viewOriginalAttachment = async () => {
        if (originalFileName && attachmentFileName) {
            const blob = await processesService.getProcessAttachment(initialData?.id);
            const blodPdf = new Blob([blob], { type: 'application/pdf' });
            const url = URL.createObjectURL(blodPdf);
            const link = document.createElement("a");
            link.style = "display: none";
            link.href = url;
            link.target = "_blank";
            link.rel = "noreferrer";
            if (String(navigator.userAgent).toLowerCase().includes("mozilla")) {
                link.download = String(initialData?.id || "anexo processo");
            }
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        }
    }

    return (
        <Flex gap="5" direction="column" minH="89vh" align="center" pt={{ sm: "150px", md: "90px" }}>
            {!isEdit && duplicateSameTypeProcess && (
                <Card w="100%" maxW="1400px" bg="#FAF089">
                    <CardBody>
                        <Text color="yellow.700" wordBreak="break-word">
                            {t(`${formTranslationKey}.duplicate-same-type-warning`, {
                                type_label: duplicateWarningTypeLabel,
                                process_number: String(
                                    duplicateSameTypeProcess.numero ?? duplicateSameTypeProcess.id
                                ),
                                interpolation: { escapeValue: false },
                            })}
                        </Text>
                    </CardBody>
                </Card>
            )}
            {/*edit-actions*/}
            {isEdit && (
                <Flex w="100%" px="30px" justify="flex-end" align="end" gap="4" direction={{ sm: "column", md: "row" }}>
                    <Stack direction="row" spacing="10px">
                        <Menu>
                            <MenuButton
                                w="125px"
                                variant="list-primary"
                                rightIcon={<IoIosArrowDown />}
                                as={Button}
                            >
                                {t("system.labels.actions")}
                            </MenuButton>

                            <MenuList variant="light">
                                <MenuItem icon={<AddIcon />}>
                                    {t("process.edit.actions.print-ca")}
                                </MenuItem>
                                <MenuItem icon={<AddIcon />} onClick={aitDetail}>
                                    {t("process.edit.actions.ait-details")}
                                </MenuItem>
                                {originalFileName && attachmentFileName && (
                                    <MenuItem icon={<DownloadIcon />} onClick={viewOriginalAttachment}>
                                        {t("process.edit.actions.view-attachment")}
                                    </MenuItem>)}
                            </MenuList>
                        </Menu>
                    </Stack>
                </Flex>
            )}
            {/*Vehicle card*/}
            <Card overflowX={{ sm: "scroll", lg: "hidden" }}>
                <CardHeader mb="20px">
                    <Text color={textColor} fontSize="lg" fontWeight="bold">
                        {t(`${formTranslationKey}.title.infractions-data`)}
                    </Text>
                </CardHeader>
                <CardBody>
                    {formData && !loading ? (
                        <Stack w="100%" direction="column" spacing={cardSpacing}>
                            {/*vehicle-plate*/}
                            <Stack alignSelf="flex-start" spacing={cardSpacing} direction="row" minW="50%" alignItems="flex-start">
                                {/*numero_ait*/}
                                <FormControl isRequired isInvalid={inputError[ids.aitNumber]}>
                                    <Input
                                        fontSize="sm"
                                        id={ids.aitNumber}
                                        name={ids.aitNumber}
                                        value={formData[ids.aitNumber].value.toUpperCase()}
                                        onChange={handleInputChange}
                                        disabled={isEdit}
                                        placeholder={t(
                                            `${formTranslationKey}.fields.ait-number.placeholder`
                                        )}
                                    />
                                    <FormErrorMessage>
                                        {t(inputError[ids.aitNumber])}
                                    </FormErrorMessage>
                                </FormControl>
                                <LightMode>
                                    <Button
                                        variant="dark"
                                        fontSize="sm"
                                        isDisabled={
                                            inputError[ids.aitNumber] ||
                                            !formData[ids.aitNumber].value
                                        }
                                        leftIcon={<SearchIcon />}
                                        onClick={handleAitSearch}
                                        minW="fit-content"
                                        _hover={isFormInvalid() ? {} : undefined}
                                    >
                                        <Text>
                                            {t(`${formTranslationKey}.fields.ait-number.label`)}
                                        </Text>
                                    </Button>
                                </LightMode>
                            </Stack>
                            <Stack spacing={formSpacing} direction={formDirection}>
                                {/*ait-vehicle-plate*/}
                                <FormControl isRequired isInvalid={inputError[ids.infracaoPlacaVeiculo]}>
                                    <FormLabel fontSize="xs" fontWeight="bold">
                                        {t(`${formTranslationKey}.fields.vehicle-plate.label`)}
                                    </FormLabel>
                                    <Input
                                        fontSize="sm"
                                        id={ids.infracaoPlacaVeiculo}
                                        value={formData[ids.infracaoPlacaVeiculo].value}
                                        disabled={true}
                                    />
                                    <FormErrorMessage>
                                        {t(inputError[ids.infracaoPlacaVeiculo])}
                                    </FormErrorMessage>
                                </FormControl>

                                {/*vehicle-uf-plate*/}
                                <FormControl isRequired isInvalid={inputError[ids.infracaoUfPlacaVeiculo]}>
                                    <FormLabel fontSize="xs" fontWeight="bold">
                                        {t(`${formTranslationKey}.fields.vehicle-uf-plate.label`)}
                                    </FormLabel>
                                    <Input
                                        fontSize="sm"
                                        id={ids.infracaoUfPlacaVeiculo}
                                        value={formData[ids.infracaoUfPlacaVeiculo].value?.toUpperCase() || ""}
                                        disabled={true}
                                    />

                                    <FormErrorMessage>
                                        {t(inputError[ids.infracaoUfPlacaVeiculo])}
                                    </FormErrorMessage>
                                </FormControl>

                                {/*vehicle-owner-name*/}
                                <FormControl isRequired isInvalid={inputError[ids.infracaoNomeProprietario]}>
                                    <FormLabel fontSize="xs" fontWeight="bold">
                                        {t(`${formTranslationKey}.fields.ait-owner-name.label`)}
                                    </FormLabel>
                                    <Input
                                        fontSize="sm"
                                        id={ids.infracaoNomeProprietario}
                                        value={formData[ids.infracaoNomeProprietario].value}
                                        disabled={true}
                                    />
                                    <FormErrorMessage>
                                        {t(inputError[ids.infracaoNomeProprietario])}
                                    </FormErrorMessage>
                                </FormControl>

                                {/*vehicle-model*/}
                                <FormControl isRequired isInvalid={inputError[ids.infracaoMarcaModelo]}>
                                    <FormLabel fontSize="xs" fontWeight="bold">
                                        {t(`${formTranslationKey}.fields.vehicle-model.label`)}
                                    </FormLabel>
                                    <Input
                                        fontSize="sm"
                                        id={ids.infracaoMarcaModelo}
                                        value={formData[ids.infracaoMarcaModelo].value}
                                        disabled={true}
                                    />
                                    <FormErrorMessage>
                                        {t(inputError[ids.infracaoMarcaModelo])}
                                    </FormErrorMessage>
                                </FormControl>
                            </Stack>
                            <Stack spacing={formSpacing} direction={formDirection}>
                                {/*logradouro_local_infracao*/}
                                <FormControl isRequired isInvalid={inputError[ids.infracaoLogradouro]}>
                                    <FormLabel fontSize="xs" fontWeight="bold">
                                        {t(`${formTranslationKey}.fields.ait-address.label`)}
                                    </FormLabel>
                                    <Input
                                        fontSize="sm"
                                        id={ids.infracaoLogradouro}
                                        value={formData[ids.infracaoLogradouro].value}
                                        disabled={true}
                                    />
                                </FormControl>

                                {/*infracao*/}
                                <FormControl>
                                    <FormLabel fontSize="xs" fontWeight="bold">
                                        {t(`${formTranslationKey}.fields.ait.label`)}
                                    </FormLabel>
                                    <Input
                                        fontSize="sm"
                                        id={ids.infracao}
                                        value={formData[ids.infracao].value}
                                        disabled={true}
                                    />
                                </FormControl>

                                {/*data_infracao*/}
                                <FormControl isRequired isInvalid={inputError[ids.infracaoDataInfracao]}>
                                    <FormLabel fontSize="xs" fontWeight="bold">
                                        {t(`${formTranslationKey}.fields.ait-date.label`)}
                                    </FormLabel>
                                    <DateTime
                                        dateFormat={DATE_FORMAT}
                                        timeFormat={false}
                                        locale={LOCALE_PT_BR}
                                        value={
                                            formData[ids.infracaoDataInfracao].value
                                                ? moment(formData[ids.infracaoDataInfracao].value, DATE_FORMAT_ISO)
                                                : null
                                        }
                                        renderInput={(props) => (
                                            <Input
                                                {...props}
                                                fontSize="sm"
                                                variant="main"
                                                disabled={true}
                                            />
                                        )}
                                    />
                                </FormControl>
                            </Stack>
                        </Stack>
                    ) : (
                        <Loading h="300px" w="100%" />
                    )}
                </CardBody>
            </Card>
            <Stack w="100%" direction={{ sm: "column", md: "row" }} spacing={cardSpacing}>
                <Card flex="1" minW="0" overflowX={{ sm: "scroll", lg: "hidden" }} minH="49vh">
                    <CardHeader mb="20px">
                        <Text color={textColor} fontSize="lg" fontWeight="bold">
                            {t(`${formTranslationKey}.title.process-data`)}
                        </Text>
                    </CardHeader>
                    <CardBody>
                        {
                            formData && !loading ? (
                                <Stack direction="column">
                                    {/*tipo*/}
                                    <FormControl isRequired isInvalid={inputError[ids.processTipo]}>
                                        <FormLabel fontSize="xs" fontWeight="bold">
                                            {t(`${formTranslationKey}.fields.process-type.label`)}
                                        </FormLabel>
                                        <Select
                                            fontSize="sm"
                                            id={ids.processTipo}
                                            name={ids.processTipo}
                                            value={formData[ids.processTipo].value}
                                            disabled={isEdit || !aitInfoCheck}
                                            onChange={handleInputChange}
                                        >
                                            <option value="">Selecione</option>
                                            {types && types.map(type => (
                                                <option key={type.key} value={type.key}>
                                                    {t(`${formTranslationKey}.process-types.${type.label}`)}
                                                </option>
                                            ))}
                                        </Select>
                                        <FormErrorMessage>
                                            {t(inputError[ids.processTipo])}
                                        </FormErrorMessage>
                                    </FormControl>

                                    {/*cpf_cnpj_condutor*/}
                                    <FormControl isRequired isInvalid={!!inputError[ids.cpfCnpjCondutor]}>
                                        <FormLabel fontSize="xs" fontWeight="bold">
                                            {t(`${formTranslationKey}.fields.driver-cpf.label`)}
                                        </FormLabel>
                                        <Input
                                            fontSize="sm"
                                            id={ids.cpfCnpjCondutor}
                                            maxLength={14}
                                            value={formData[ids.cpfCnpjCondutor].value}
                                            onChange={handleInputChange}
                                            disabled={!aitInfoCheck}
                                        />
                                        <FormErrorMessage>
                                            {t(inputError[ids.cpfCnpjCondutor])}
                                        </FormErrorMessage>
                                    </FormControl>

                                    {/*nome_condutor*/}
                                    <FormControl isRequired isInvalid={inputError[ids.nomeCondutor]}>
                                        <FormLabel fontSize="xs" fontWeight="bold">
                                            {t(`${formTranslationKey}.fields.driver-name.label`)}
                                        </FormLabel>
                                        <Input
                                            fontSize="sm"
                                            id={ids.nomeCondutor}
                                            value={formData[ids.nomeCondutor].value}
                                            onChange={handleInputChange}
                                            disabled={!aitInfoCheck}
                                            maxLength={250}
                                        />
                                        <FormErrorMessage>
                                            {t(inputError[ids.nomeCondutor])}
                                        </FormErrorMessage>
                                    </FormControl>
                                    {/* registro_cnh_condutor */}
                                    <FormControl>
                                        <FormLabel fontSize="xs" fontWeight="bold">
                                            {t(`${formTranslationKey}.fields.register-cnh-license.label`)}
                                        </FormLabel>
                                        <Input
                                            fontSize="sm"
                                            id={ids.registroCnhCondutor}
                                            value={formData[ids.registroCnhCondutor].value}
                                            onChange={handleInputChange}
                                            maxLength={250}
                                        />
                                        <FormErrorMessage>
                                            {t(inputError[ids.registroCnhCondutor])}
                                        </FormErrorMessage>
                                    </FormControl>
                                    {/*data_limite_julgamento*/}
                                    <FormControl isRequired isInvalid={inputError[ids.dataLimiteJulgamento]}>
                                        <FormLabel fontSize="xs" fontWeight="bold">
                                            {t(`${formTranslationKey}.fields.trial-date-limit.label`)}
                                        </FormLabel>
                                        <DateTime
                                            dateFormat={DATE_FORMAT}
                                            timeFormat={false}
                                            locale={LOCALE_PT_BR}
                                            value={formData[ids.dataLimiteJulgamento].value
                                                ? moment(formData[ids.dataLimiteJulgamento].value, DATE_FORMAT_ISO)
                                                : null}
                                            renderInput={(props) => (
                                                <Input
                                                    {...props}
                                                    fontSize="sm"
                                                    variant="main"
                                                    disabled={!aitInfoCheck}
                                                />
                                            )}
                                            onChange={(date) =>
                                                handleJudgementLimitDateChange(
                                                    moment(date).isValid() ? moment(date).format(DATE_FORMAT_ISO) : ""
                                                )
                                            }
                                        />
                                        <FormErrorMessage>
                                            {t(inputError[ids.dataLimiteJulgamento])}
                                        </FormErrorMessage>
                                    </FormControl>

                                    {/*observacoes*/}
                                    <FormControl>
                                        <FormLabel fontSize="xs" fontWeight="bold">
                                            {t(`${formTranslationKey}.fields.observations.label`)}
                                        </FormLabel>
                                        <Textarea
                                            fontSize="sm"
                                            id={ids.observacoes}
                                            value={formData[ids.observacoes].value}
                                            onChange={handleInputChange}
                                            disabled={!aitInfoCheck}
                                            maxLength={250}
                                        />
                                        <FormErrorMessage>
                                            {t(inputError[ids.observacoes])}
                                        </FormErrorMessage>
                                    </FormControl>

                                    {/*anexo*/}
                                    <FileAttachmentMultipleInput ref={attachmentRef} nameVariable={attachmentFileName} originalFileName={originalFileName} acceptedTypes={['application/pdf', 'image/jpeg']} onClearAction={removeProcessAttachment} showClearButton={false} showPrintButton={false} showDownloadButton={false} isDisabled={!aitInfoCheck} />
                                </Stack>
                            ) : (<Loading h="300px" w="100%" />)
                        }
                    </CardBody>
                </Card>
                <Card flex="1" minW="0" overflowX={{ sm: "scroll", lg: "hidden" }} minH="49vh">
                    <CardHeader mb="20px">
                        <Text color={textColor} fontSize="lg" fontWeight="bold">
                            {t(`${formTranslationKey}.title.requester-data`)}
                        </Text>
                    </CardHeader>
                    <CardBody>
                        {
                            formData && !loading ? (
                                <Stack direction="column">
                                    {/*cep_requerente*/}
                                    <FormControl isRequired isInvalid={!!inputError[ids.cepRequerente]}>
                                        <FormLabel fontSize="xs" fontWeight="bold">
                                            {t(`${formTranslationKey}.fields.requester-cep.label`)}
                                        </FormLabel>
                                        <Input
                                            fontSize="sm"
                                            id={ids.cepRequerente}
                                            maxLength={9}
                                            value={formData[ids.cepRequerente].value}
                                            onChange={handleInputChange}
                                            disabled={!aitInfoCheck}
                                        />
                                        <FormErrorMessage>
                                            {t(inputError[ids.cepRequerente])}
                                        </FormErrorMessage>
                                    </FormControl>

                                    {/*logradouro_requerente*/}
                                    <FormControl isRequired isInvalid={inputError[ids.aitLogradouroRequerente]}>
                                        <FormLabel fontSize="xs" fontWeight="bold">
                                            {t(`${formTranslationKey}.fields.requester-street.label`)}
                                        </FormLabel>
                                        <Input
                                            fontSize="sm"
                                            id={ids.aitLogradouroRequerente}
                                            value={formData[ids.aitLogradouroRequerente].value}
                                            onChange={handleInputChange}
                                            disabled={!aitInfoCheck}
                                            maxLength={250}
                                        />
                                        <FormErrorMessage>
                                            {t(inputError[ids.aitLogradouroRequerente])}
                                        </FormErrorMessage>
                                    </FormControl>

                                    {/*numero_requerente*/}
                                    <FormControl isRequired isInvalid={inputError[ids.aitNumeroRequerente]}>
                                        <FormLabel fontSize="xs" fontWeight="bold">
                                            {t(`${formTranslationKey}.fields.requester-street-number.label`)}
                                        </FormLabel>
                                        <Input
                                            fontSize="sm"
                                            id={ids.aitNumeroRequerente}
                                            value={formData[ids.aitNumeroRequerente].value}
                                            onChange={handleInputChange}
                                            disabled={!aitInfoCheck}
                                            maxLength={250}
                                        />
                                        <FormErrorMessage>
                                            {t(inputError[ids.aitNumeroRequerente])}
                                        </FormErrorMessage>
                                    </FormControl>

                                    {/*complemento_requerente*/}
                                    <FormControl>
                                        <FormLabel fontSize="xs" fontWeight="bold">
                                            {t(`${formTranslationKey}.fields.requester-addredd-info.label`)}
                                        </FormLabel>
                                        <Input
                                            fontSize="sm"
                                            id={ids.aitComplementoRequerente}
                                            value={formData[ids.aitComplementoRequerente].value}
                                            onChange={handleInputChange}
                                            disabled={!aitInfoCheck}
                                            maxLength={250}
                                        />
                                        <FormErrorMessage>
                                            {t(inputError[ids.aitComplementoRequerente])}
                                        </FormErrorMessage>
                                    </FormControl>

                                    {/*bairro_requerente*/}
                                    <FormControl isRequired isInvalid={inputError[ids.aitBairroRequerente]}>
                                        <FormLabel fontSize="xs" fontWeight="bold">
                                            {t(`${formTranslationKey}.fields.requester-addredd-neighbour.label`)}
                                        </FormLabel>
                                        <Input
                                            fontSize="sm"
                                            id={ids.aitBairroRequerente}
                                            value={formData[ids.aitBairroRequerente].value}
                                            onChange={handleInputChange}
                                            disabled={!aitInfoCheck}
                                            maxLength={250}
                                        />
                                        <FormErrorMessage>
                                            {t(inputError[ids.aitBairroRequerente])}
                                        </FormErrorMessage>
                                    </FormControl>

                                    {/*cidade_requerente*/}
                                    <FormControl isRequired isInvalid={inputError[ids.aitMunicipioRequerente]}>
                                        <FormLabel fontSize="xs" fontWeight="bold">
                                            {t(`${formTranslationKey}.fields.requester-addredd-city.label`)}
                                        </FormLabel>
                                        <Input
                                            fontSize="sm"
                                            id={ids.aitMunicipioRequerente}
                                            value={formData[ids.aitMunicipioRequerente].value}
                                            onChange={handleInputChange}
                                            disabled={!aitInfoCheck}
                                            maxLength={250}
                                        />
                                        <FormErrorMessage>
                                            {t(inputError[ids.aitMunicipioRequerente])}
                                        </FormErrorMessage>
                                    </FormControl>

                                    {/*uf_requerente*/}
                                    <FormControl isRequired isInvalid={inputError[ids.aitEstadoRequerente]}>
                                        <FormLabel fontSize="xs" fontWeight="bold">
                                            {t(`${formTranslationKey}.fields.requester-addredd-sate.label`)}
                                        </FormLabel>
                                        <Select
                                            fontSize="sm"
                                            id={ids.aitEstadoRequerente}
                                            placeholder={t(`${formTranslationKey}.fields.vehicle-uf-plate.placeholder`)}
                                            value={formData[ids.aitEstadoRequerente].value}
                                            onChange={handleInputChange}
                                            disabled={!aitInfoCheck}
                                        >
                                            {ufs.map((uf) => (
                                                <option key={uf.value} value={uf.value}>
                                                    {uf.value} - {uf.label}
                                                </option>
                                            ))}
                                        </Select>
                                        <FormErrorMessage>
                                            {t(inputError[ids.aitEstadoRequerente])}
                                        </FormErrorMessage>
                                    </FormControl>

                                    {/*telefone_contato*/}
                                    <FormControl isInvalid={!!inputError[ids.telefoneContato]}>
                                        <FormLabel fontSize="xs" fontWeight="bold">
                                            {t(`${formTranslationKey}.fields.requester-phone.label`)}
                                        </FormLabel>
                                        <Input
                                            fontSize="sm"
                                            id={ids.telefoneContato}
                                            value={formData[ids.telefoneContato].value}
                                            onChange={handleInputChange}
                                            maxLength={15}
                                            disabled={!aitInfoCheck}
                                        />
                                        <FormErrorMessage>
                                            {inputError[ids.telefoneContato]}
                                        </FormErrorMessage>
                                    </FormControl>

                                    {/*email_contato*/}
                                    <FormControl isInvalid={!!inputError[ids.emailContato]}>
                                        <FormLabel fontSize="xs" fontWeight="bold">
                                            {t(`${formTranslationKey}.fields.requester-email.label`)}
                                        </FormLabel>
                                        <Input
                                            fontSize="sm"
                                            id={ids.emailContato}
                                            value={formData[ids.emailContato].value}
                                            onChange={handleInputChange}
                                            disabled={!aitInfoCheck}
                                            maxLength={250}
                                        />
                                        <FormErrorMessage>
                                            {inputError[ids.emailContato]}
                                        </FormErrorMessage>
                                    </FormControl>
                                </Stack>
                            ) : (<Loading h="300px" w="100%" />)
                        }
                    </CardBody>
                </Card>
            </Stack>
            <Card overflowX={{ sm: "scroll", lg: "hidden" }}>
                <CardHeader mb="20px">
                    <Flex justify="space-between" align="center">
                        <Text color={textColor} fontSize="lg" fontWeight="bold">
                            {t(`${formTranslationKey}.title.attachments-data`)}
                        </Text>
                        <Button
                            variant="dark"
                            fontSize="sm"
                            onClick={handleAddAttachment}
                            disabled={!aitInfoCheck}
                            _hover={!aitInfoCheck ? {} : undefined}
                        >
                            {t("process.form.actions.add-attachment")}
                        </Button>
                    </Flex>
                </CardHeader>
                {/*attachments*/}
                <CardBody>
                    {
                        formData && !loading ? (
                            <Stack spacing={cardSpacing}>
                                {attachments.map((attachment, index) => (
                                    <Stack key={index} direction={{ base: "column", md: "row" }} spacing="15px" align="end">
                                        <FormControl isRequired width={{ base: "100%", md: "30%" }}>
                                            <FormLabel fontSize="xs" fontWeight="bold">
                                                {t(`${formTranslationKey}.fields.process-type.label`)}
                                            </FormLabel>
                                            <Select
                                                fontSize="sm"
                                                value={attachment.tipo}
                                                disabled={!aitInfoCheck}
                                                onChange={(e) => handleAttachmentChange(index, "tipo", e.target.value)}
                                            >
                                                <option value=""></option>
                                                {documentTypes?.map((type) => (
                                                    <option key={type.id} value={type.id}>
                                                        {type.descricao}
                                                    </option>
                                                ))}
                                            </Select>
                                        </FormControl>

                                        <FormControl width={{ base: "100%", md: "50%" }}>
                                            <FormLabel fontSize="xs" fontWeight="bold">
                                                {t("process.form.fields.observations.label")}
                                            </FormLabel>
                                            <Input
                                                maxLength={250}
                                                fontSize="sm"
                                                value={attachment.observacoes}
                                                disabled={!aitInfoCheck}
                                                onChange={(e) => handleAttachmentChange(index, "observacoes", e.target.value)}
                                            />
                                        </FormControl>

                                        <IconButton
                                            aria-label="Remove Attachment"
                                            icon={<DeleteIcon />}
                                            onClick={() => handleRemoveAttachment(index)}
                                            colorScheme="red"
                                            variant="outline"
                                            disabled={!aitInfoCheck}
                                        />
                                    </Stack>
                                ))}
                            </Stack>
                        ) : (
                            <Loading h="300px" w="100%" />
                        )
                    }
                </CardBody>
            </Card>
            <Stack w="100%" direction="row" justify="space-between">
                <Button w="125px"
                    variant="no-effects"
                    bg="gray.100"
                    color="gray.700"
                    fontSize="sm"
                    fontWeight="bold"
                    onClick={redirectToList}
                >
                    <Text>
                        {t("system.actions.cancel")}
                    </Text>
                </Button>

                <Button
                    w="125px"
                    variant="dark"
                    fontSize="sm"
                    isDisabled={isFormInvalid()}
                    onClick={handleSave}
                    _hover={isFormInvalid() ? {} : undefined}
                >
                    <Text>
                        {t("system.actions.save")}
                    </Text>
                </Button>
            </Stack>
            <ConfirmDialog ref={dialogRef} onConfirm={handleConfirmRemoveAttachment} confirmButtonProps={{ colorScheme: "red" }} />
        </Flex>
    );
}

export default ProcessNew;
